import React, { useMemo } from 'react';
import { motion } from 'framer-motion';
import {
  FileText,
  Download,
  Printer,
  Share2,
  CheckCircle2,
  AlertTriangle,
  Clock,
  TrendingUp,
  Activity,
  Layers,
  ChevronRight
} from 'lucide-react';
import { Layout } from '@/components/Layout';
import {
  CostAnalysisChart,
  RegionalChart,
  TrendChart
} from '@/components/Charts';
import { KPICards } from '@/components/KPICards';
import { useHealthcareData } from '@/hooks/useHealthcareData';
import { IMAGES } from '@/assets/images';
import { Button } from '@/components/ui/button';
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { Separator } from '@/components/ui/separator';

const staggerContainer = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.1
    }
  }
};

const fadeInUp = {
  hidden: { opacity: 0, y: 20 },
  visible: { opacity: 1, y: 0 }
};

export default function Reports() {
  const { kpis, data, regionalStats, isLoading } = useHealthcareData();

  // Calculate treatment outcome summary
  const outcomeStats = useMemo(() => {
    if (!data.length) return { critical: 0, recovering: 0, stable: 0, total: 0 };
    return data.reduce(
      (acc, patient) => {
        const status = patient.status?.toLowerCase() || 'stable';
        if (status === 'critical') acc.critical++;
        else if (status === 'recovering') acc.recovering++;
        else acc.stable++;
        acc.total++;
        return acc;
      },
      { critical: 0, recovering: 0, stable: 0, total: 0 }
    );
  }, [data]);

  const performanceScore = useMemo(() => {
    if (!outcomeStats.total) return 0;
    return Math.round(((outcomeStats.recovering + outcomeStats.stable) / outcomeStats.total) * 100);
  }, [outcomeStats]);

  if (isLoading) {
    return (
      <Layout>
        <div className="flex h-[80vh] items-center justify-center">
          <div className="flex flex-col items-center gap-4">
            <Activity className="h-12 w-12 animate-pulse text-primary" />
            <p className="text-muted-foreground animate-pulse">Generating clinical reports...</p>
          </div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="relative min-h-screen space-y-8 p-6 lg:p-10">
        {/* Medical Background Theme Overlay */}
        <div 
          className="pointer-events-none fixed inset-0 z-0 opacity-[0.03]"
          style={{ 
            backgroundImage: `url(${IMAGES.MEDICAL_BG_8})`, 
            backgroundSize: 'cover', 
            backgroundPosition: 'center' 
          }}
        />

        {/* Header Section */}
        <div className="relative z-10 flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
          <div>
            <div className="flex items-center gap-2 text-primary mb-1">
              <FileText className="h-5 w-5" />
              <span className="text-sm font-semibold tracking-wider uppercase">Executive Summary</span>
            </div>
            <h1 className="text-3xl font-bold tracking-tight text-foreground">Healthcare Performance Report</h1>
            <p className="text-muted-foreground mt-1">Comprehensive analysis of patient outcomes, costs, and hospital efficiency for Q1 2026.</p>
          </div>
          <div className="flex flex-wrap gap-3">
            <Button variant="outline" size="sm" className="gap-2 bg-background/50 backdrop-blur-sm">
              <Share2 className="h-4 w-4" /> Share
            </Button>
            <Button variant="outline" size="sm" className="gap-2 bg-background/50 backdrop-blur-sm">
              <Printer className="h-4 w-4" /> Print
            </Button>
            <Button size="sm" className="gap-2 shadow-lg shadow-primary/20">
              <Download className="h-4 w-4" /> Export PDF
            </Button>
          </div>
        </div>

        {/* Key Metrics Dashboard */}
        <div className="relative z-10">
          <KPICards metrics={kpis} />
        </div>

        <motion.div 
          variants={staggerContainer} 
          initial="hidden" 
          animate="visible"
          className="relative z-10 grid gap-6 md:grid-cols-2 lg:grid-cols-3"
        >
          {/* Hospital Efficiency Score Card */}
          <motion.div variants={fadeInUp} className="lg:col-span-1">
            <Card className="h-full border-none bg-card/60 backdrop-blur-xl shadow-xl ring-1 ring-white/10 overflow-hidden">
              <div className="absolute top-0 right-0 p-4 opacity-10">
                <Layers className="h-24 w-24" />
              </div>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Activity className="h-5 w-5 text-accent" />
                  Clinical Efficiency
                </CardTitle>
                <CardDescription>Overall performance index</CardDescription>
              </CardHeader>
              <CardContent className="flex flex-col items-center justify-center space-y-6 pb-8">
                <div className="relative flex items-center justify-center">
                  <svg className="h-40 w-40">
                    <circle
                      className="text-muted/20"
                      strokeWidth="10"
                      stroke="currentColor"
                      fill="transparent"
                      r="70"
                      cx="80"
                      cy="80"
                    />
                    <motion.circle
                      className="text-primary"
                      strokeWidth="10"
                      strokeDasharray={440}
                      initial={{ strokeDashoffset: 440 }}
                      animate={{ strokeDashoffset: 440 - (440 * performanceScore) / 100 }}
                      transition={{ duration: 1.5, ease: "easeOut" }}
                      strokeLinecap="round"
                      stroke="currentColor"
                      fill="transparent"
                      r="70"
                      cx="80"
                      cy="80"
                    />
                  </svg>
                  <div className="absolute text-center">
                    <span className="text-4xl font-bold">{performanceScore}%</span>
                    <p className="text-xs text-muted-foreground uppercase">Performance</p>
                  </div>
                </div>
                <div className="w-full space-y-3">
                  <div className="flex justify-between text-sm">
                    <span className="text-muted-foreground">Recovery Target</span>
                    <span className="font-medium">85%</span>
                  </div>
                  <Progress value={performanceScore} className="h-2" />
                </div>
              </CardContent>
            </Card>
          </motion.div>

          {/* Cost Effectiveness Analysis */}
          <motion.div variants={fadeInUp} className="lg:col-span-2">
            <Card className="h-full border-none bg-card/60 backdrop-blur-xl shadow-xl ring-1 ring-white/10">
              <CardHeader className="flex flex-row items-center justify-between">
                <div>
                  <CardTitle>Cost Effectiveness</CardTitle>
                  <CardDescription>Monthly expenditure vs Treatment Success</CardDescription>
                </div>
                <Badge variant="outline" className="bg-accent/10 text-accent border-accent/20">
                  Live Data
                </Badge>
              </CardHeader>
              <CardContent>
                <div className="h-[300px] w-full">
                  <CostAnalysisChart data={data} title="Expenditure Distribution" />
                </div>
              </CardContent>
            </Card>
          </motion.div>

          {/* Regional Performance */}
          <motion.div variants={fadeInUp} className="lg:col-span-2">
            <Card className="border-none bg-card/60 backdrop-blur-xl shadow-xl ring-1 ring-white/10">
              <CardHeader>
                <CardTitle>Regional Distribution</CardTitle>
                <CardDescription>Patient load and financial impact by territory</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="h-[350px] w-full">
                  <RegionalChart data={regionalStats} title="Territory Performance" />
                </div>
              </CardContent>
            </Card>
          </motion.div>

          {/* Insights & Outcomes */}
          <motion.div variants={fadeInUp} className="lg:col-span-1">
            <Card className="h-full border-none bg-primary text-primary-foreground shadow-2xl shadow-primary/20">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <TrendingUp className="h-5 w-5" />
                  Predictive Insights
                </CardTitle>
                <CardDescription className="text-primary-foreground/70">AI-driven clinical forecast</CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                <div className="space-y-4">
                  <div className="flex items-start gap-3 rounded-lg bg-white/10 p-3 backdrop-blur-sm">
                    <CheckCircle2 className="mt-1 h-5 w-5 shrink-0 text-white" />
                    <div>
                      <p className="text-sm font-semibold">Reduced Readmissions</p>
                      <p className="text-xs text-white/70">Patient recovery monitoring has reduced 30-day readmissions by 14.2%.</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3 rounded-lg bg-white/10 p-3 backdrop-blur-sm">
                    <AlertTriangle className="mt-1 h-5 w-5 shrink-0 text-yellow-300" />
                    <div>
                      <p className="text-sm font-semibold">Cost Outliers Detected</p>
                      <p className="text-xs text-white/70">Southeast region shows 8% higher average charges than predicted baseline.</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3 rounded-lg bg-white/10 p-3 backdrop-blur-sm">
                    <Clock className="mt-1 h-5 w-5 shrink-0 text-white" />
                    <div>
                      <p className="text-sm font-semibold">Efficiency Peak</p>
                      <p className="text-xs text-white/70">Hospital throughput is currently at 94% capacity with optimal staff ratio.</p>
                    </div>
                  </div>
                </div>
                <Separator className="bg-white/20" />
                <Button variant="secondary" className="w-full group">
                  View Full Audit 
                  <ChevronRight className="ml-2 h-4 w-4 transition-transform group-hover:translate-x-1" />
                </Button>
              </CardContent>
            </Card>
          </motion.div>

          {/* Treatment Trends */}
          <motion.div variants={fadeInUp} className="lg:col-span-3">
            <Card className="border-none bg-card/60 backdrop-blur-xl shadow-xl ring-1 ring-white/10">
              <CardHeader className="flex flex-row items-center justify-between">
                <div>
                  <CardTitle>Outcome Trends</CardTitle>
                  <CardDescription>Historical patient recovery and stabilization velocity</CardDescription>
                </div>
                <div className="flex gap-2">
                  <Badge variant="secondary">Daily</Badge>
                  <Badge variant="outline">Weekly</Badge>
                  <Badge variant="outline">Monthly</Badge>
                </div>
              </CardHeader>
              <CardContent>
                <div className="h-[400px] w-full">
                  <TrendChart data={data} title="Treatment Progression" />
                </div>
              </CardContent>
            </Card>
          </motion.div>
        </motion.div>

        {/* Detailed Report Footer Section */}
        <motion.div 
          initial={{ opacity: 0 }} 
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          className="relative z-10 rounded-2xl bg-muted/30 border border-border/50 p-8 text-center backdrop-blur-sm"
        >
          <div className="mx-auto max-w-2xl space-y-4">
            <div className="flex justify-center mb-4">
              <img 
                src={IMAGES.HEALTHCARE_DATA_4} 
                alt="Healthcare Data" 
                className="h-20 w-20 rounded-xl object-cover shadow-lg grayscale hover:grayscale-0 transition-all duration-500"
              />
            </div>
            <h2 className="text-2xl font-bold">Need more granular data?</h2>
            <p className="text-muted-foreground">
              Our 2026 Analytics Engine provides deep-dive capabilities for individual patient cohorts, insurance providers, and surgical outcomes.
            </p>
            <div className="flex justify-center gap-4 pt-4">
              <Button variant="link" className="text-primary">View Methodology</Button>
              <Button variant="link" className="text-primary">Contact Data Team</Button>
              <Button variant="link" className="text-primary">Download CSV Raw</Button>
            </div>
          </div>
        </motion.div>
      </div>
    </Layout>
  );
}
